import { useEffect, useMemo, useState } from 'react';

import clientSideLogger from '@/domains/core/observability/loggers/clientSideLogger';
import type { EligibilityCoupon } from '@/domains/loyalty/modules/CouponEligibility/interfaces/EligibilityCoupon';
import { processBundleCoupons } from '@/domains/productDiscovery/Recommendation/modules/ProductBundle/components/Coupon/utils/processBundleCoupons';
import { getFullBundlePrice } from '@/domains/productDiscovery/Recommendation/modules/ProductBundle/utils/formatters';

import type { IProduct } from '../../../context/ProductBundle/productBundle.interfaces';
import fetchBundleCoupons from '../services/fetchBundleCoupons';

export const useBundleCoupons = (
  productList: Array<IProduct>,
  isCouponABEnabled: boolean,
  mainProductHasCoupon: boolean,
): { bundleCoupon: EligibilityCoupon | false } => {
  const [bundleCoupon, setBundleCoupon] = useState<EligibilityCoupon | false>(
    false,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isFetched, setIsFetched] = useState(false);

  const totalPrice: string = useMemo(
    () => getFullBundlePrice(productList),
    [productList],
  );

  useEffect(() => {
    const getBundleCoupons = async () => {
      setIsLoading(true);
      try {
        const modelIds = productList.map((product) => product.modelId);

        const allSellersAreTheSame = productList.every(
          (val, i, arr) => val.sellerId === arr[0].sellerId,
        );

        const response = await fetchBundleCoupons(
          allSellersAreTheSame
            ? { sellerId: productList[0].sellerId, modelIds }
            : {},
        );

        setBundleCoupon(processBundleCoupons(response, parseFloat(totalPrice)));
        setIsLoading(false);
        setIsFetched(true);
      } catch (err) {
        clientSideLogger.error(err);
      }
    };

    if (
      isCouponABEnabled &&
      !mainProductHasCoupon &&
      productList.length > 0 &&
      !isLoading &&
      !isFetched
    ) {
      getBundleCoupons();
    }
  }, [
    productList,
    totalPrice,
    isCouponABEnabled,
    mainProductHasCoupon,
    isLoading,
    isFetched,
  ]);

  return { bundleCoupon };
};
